// NavContext — bridges the existing state-based AdminPanel navigation
// to URL-based React Router routing without rewriting AdminPanel internally.
//
// AdminPanel uses a single `active` string (e.g. "leads.all", "properties.list")
// to drive which screen renders. We want URLs like /super-admin/leads/all to
// reflect that state so users can deep-link, bookmark, and use back/forward.
//
// This provider lives *inside* each role-prefixed route. It:
//   1. Reads the URL on mount → derives initial `active`
//   2. Exposes `active` + `setActive` via context
//   3. When `setActive` is called, also calls navigate() to sync URL
//   4. When URL changes (browser back/forward), syncs state back
//
// AdminPanel just needs to replace its internal `useState('dashboard')` with
// `useNav()` — a one-line change. See INSTALL.md for the patch.

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const NavContext = createContext(null);

// Convert URL path → active string.
// "/super-admin/leads/all"  → "leads.all"
// "/super-admin/dashboard"  → "dashboard"
// "/super-admin"            → "dashboard" (default)
function pathToActive(pathname, rolePrefix, defaultActive) {
  if (!pathname.startsWith(rolePrefix)) return defaultActive;
  const tail = pathname.slice(rolePrefix.length).replace(/^\/+/, '').replace(/\/+$/, '');
  if (!tail) return defaultActive;
  return tail.replace(/\//g, '.');
}

// Convert active string → URL path.
// "leads.all"   → "/super-admin/leads/all"
// "dashboard"   → "/super-admin/dashboard"
function activeToPath(active, rolePrefix) {
  return `${rolePrefix}/${active.replace(/\./g, '/')}`;
}

export function NavProvider({ rolePrefix, defaultActive = 'dashboard', children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [active, _setActive] = useState(() =>
    pathToActive(location.pathname, rolePrefix, defaultActive),
  );

  // URL → state (browser back/forward, deep-link)
  useEffect(() => {
    const next = pathToActive(location.pathname, rolePrefix, defaultActive);
    if (next !== active) _setActive(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, rolePrefix]);

  // state → URL (clicks inside AdminPanel)
  const setActive = useCallback(
    (next) => {
      _setActive(next);
      const targetPath = activeToPath(next, rolePrefix);
      if (location.pathname !== targetPath) {
        navigate(targetPath);
      }
    },
    [navigate, rolePrefix, location.pathname],
  );

  return (
    <NavContext.Provider value={{ active, setActive, rolePrefix }}>
      {children}
    </NavContext.Provider>
  );
}

export function useNav() {
  const ctx = useContext(NavContext);
  if (!ctx) {
    // Render safely outside a NavProvider (e.g. unit tests).
    // AdminPanel falls back to its own local state in this case.
    return null;
  }
  return ctx;
}
